<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisDrawer from 'dashboard/components-next/LaisDrawer.vue';
import PostSaleAttentionPoints from './PostSaleAttentionPoints.vue';
import PostSaleCaseHistory from './PostSaleCaseHistory.vue';
import PostSaleCaseSummary from './PostSaleCaseSummary.vue';
import PostSaleDeadlineMap from './PostSaleDeadlineMap.vue';
import PostSaleNotesPanel from './PostSaleNotesPanel.vue';
import PostSaleStatusDetail from './PostSaleStatusDetail.vue';
import PostSaleStatusTimeline from './PostSaleStatusTimeline.vue';

defineProps({
  agents: { type: Array, default: () => [] },
  caseData: { type: Object, required: true },
  editingNote: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  loadingNotes: { type: Boolean, default: false },
  noteSaving: { type: Boolean, default: false },
  notes: { type: Array, default: () => [] },
  loadingTopics: { type: Boolean, default: false },
  responsibleUserId: { type: [String, Number], default: '' },
  showNoteForm: { type: Boolean, default: false },
  show: { type: Boolean, default: false },
  topics: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'assign',
  'changeStatus',
  'closeNoteForm',
  'close',
  'createNote',
  'deleteNote',
  'editNote',
  'editTopic',
  'openAnalysis',
  'requestNucleo',
  'requestCorrection',
  'saveNote',
  'update:responsibleUserId',
]);
const { t } = useI18n();
const selectedStatusEvent = ref(null);

const selectStatusEvent = event => {
  selectedStatusEvent.value = event;
};

const closeStatusDetail = () => {
  selectedStatusEvent.value = null;
};
</script>

<template>
  <LaisDrawer
    :show="show"
    :title="t('POST_SALE.DETAIL.TITLE')"
    :close-label="t('POST_SALE.ACTIONS.CLOSE')"
    @close="emit('close')"
  >
    <template #body>
      <div v-if="loading" class="p-6 text-sm text-lais-text-muted">
        {{ t('POST_SALE.COMMON.LOADING') }}
      </div>
      <div v-else class="grid gap-4">
        <PostSaleCaseSummary
          :agents="agents"
          :case-data="caseData"
          :responsible-user-id="responsibleUserId"
          @assign="emit('assign')"
          @update:responsible-user-id="emit('update:responsibleUserId', $event)"
        />

        <PostSaleStatusTimeline
          :case-data="caseData"
          @change-status="emit('changeStatus')"
          @open-analysis="emit('openAnalysis')"
          @select="selectStatusEvent"
        />

        <PostSaleDeadlineMap
          :case-data="caseData"
          @select="selectStatusEvent"
        />

        <PostSaleAttentionPoints
          :loading="loadingTopics"
          :topics="topics"
          @edit="emit('editTopic', $event)"
        />

        <PostSaleCaseHistory
          :case-data="caseData"
          @change-status="emit('changeStatus')"
          @request-correction="emit('requestCorrection')"
          @request-nucleo="emit('requestNucleo')"
        />

        <PostSaleNotesPanel
          :can-manage="caseData.permissions?.can_add_note"
          :editing-note="editingNote"
          :loading="loadingNotes"
          :notes="notes"
          :saving="noteSaving"
          :show-form="showNoteForm"
          @close-form="emit('closeNoteForm')"
          @create="emit('createNote')"
          @delete="emit('deleteNote', $event)"
          @edit="emit('editNote', $event)"
          @save="emit('saveNote', $event)"
        />
      </div>
    </template>
  </LaisDrawer>

  <PostSaleStatusDetail
    :event="selectedStatusEvent"
    :show="Boolean(selectedStatusEvent)"
    @close="closeStatusDetail"
  />
</template>
